import type { AdminDiscordGuild } from "@ternary/contracts"
import { Alert, Badge, type BadgeProps, Button } from "@ternary/ui"
import { useRef, useState } from "react"
import { useWide } from "../use-wide.js"

/**
 * Track A's guild-proof install (fix d): a 302 to Discord that comes back to the Discord page with
 * `?connected=1` or `?error=<code>`. It is the only way a server joins the organization.
 */
export const INSTALL_URL = "/api/v1/admin/discord/install"

/** Starts the install, as a link styled as a button; a disabled button for a read-only account. */
export function InstallLink({
  children,
  href = INSTALL_URL,
  size = "md",
  variant = "secondary",
  writable,
}: {
  children: string
  /** Another install path, such as one server's re-add. Default: the install. */
  href?: string
  size?: "md" | "sm"
  variant?: "primary" | "secondary"
  writable: boolean
}) {
  // An account that cannot change the settings has nothing to install: the action is hidden.
  if (!writable) return null
  return (
    <a
      className={`tny-button tny-button-${variant}${size === "sm" ? " tny-button-sm" : ""} admin-button-link`}
      href={href}
    >
      {children}
    </a>
  )
}

/** The permissions Ternary needs, in the order the card lists them. */
const PERMISSIONS = [
  ["manageRoles", "Manage Roles"],
  ["createInvite", "Create Invite"],
  ["viewChannels", "View Channels"],
  ["sendMessages", "Send Messages"],
] as const

type BotHealth = NonNullable<AdminDiscordGuild["bot"]>

/**
 * The permissions this server needs: the four, and Kick Members where the API reads it (the server
 * where a paid product removes a lapsed member from the server).
 */
function needed(bot: BotHealth) {
  return bot.kickMembers === undefined
    ? PERMISSIONS
    : ([...PERMISSIONS, ["kickMembers", "Kick Members"]] as const)
}

/**
 * A server's one status badge: "Bot missing" when the bot cannot reach it (or, for the primary, the
 * connection says so), "Needs permission" when one is missing, "Bot connected" only when every
 * permission is OK, and "Not checked" when Discord could not say.
 */
export function serverStatus(
  guild: AdminDiscordGuild,
  connected = true,
): { label: string; tone: NonNullable<BadgeProps["tone"]> } {
  const { bot } = guild
  if (guild.connected === false || !connected) return { label: "Bot missing", tone: "danger" }
  if (!bot) return { label: "Not checked", tone: "neutral" }
  return needed(bot).every(([key]) => bot[key])
    ? { label: "Bot connected", tone: "success" }
    : { label: "Needs permission", tone: "warning" }
}

/**
 * One server's bot health (G4): each permission Ternary needs, OK or Missing, and a warning Alert
 * with "Re-add the bot" when one is missing, which opens Discord on that server. A bot that cannot
 * reach the server is a warning Alert with Re-add the bot. When Discord could not say (`bot`
 * null, or absent where it was not read), a warning Alert says so and offers Check again; once the
 * check is back, focus moves to the card, whose button may be gone. The Alerts are not live
 * regions (role none), so a page of them is not read out on load; only the check's own line, a
 * status, announces its answer. It takes no generated id, so the page's other ids stay as they
 * were.
 */
export function DiscordHealth({
  checking,
  guild,
  onCheck,
  writable,
}: {
  checking: boolean
  guild: AdminDiscordGuild
  /** Loads the Discord settings again. */
  onCheck(): Promise<unknown>
  writable: boolean
}) {
  const cardRef = useRef<HTMLFieldSetElement>(null)
  const wide = useWide()
  // After a check that still found nothing (Discord said nothing, or the read failed), the note
  // says so, so a retry that changed nothing never looks like one that did not run.
  const [tried, setTried] = useState(false)
  const { bot } = guild
  // A read that said something (the permissions, or that the bot is gone) ends the attempt, so a
  // later unknown read starts over at "We couldn't" (React's adjust-state-while-rendering pattern).
  if (tried && (bot || guild.connected === false)) {
    setTried(false)
  }

  async function check() {
    if (checking) return
    await onCheck()
    setTried(true)
    cardRef.current?.focus()
  }

  const rows = bot ? (
    <dl className="admin-bot-health-rows">
      {needed(bot).map(([key, label]) => (
        <div key={key}>
          <dt>{label}</dt>
          <dd>
            <Badge tone={bot[key] ? "success" : "danger"}>{bot[key] ? "OK" : "Missing"}</Badge>
          </dd>
        </div>
      ))}
    </dl>
  ) : null
  const healthy = bot ? needed(bot).every(([key]) => bot[key]) : false
  const readd = (
    <InstallLink
      href={guild.reinstallUrl ?? `${INSTALL_URL}?guildId=${guild.id}`}
      size="sm"
      writable={writable}
    >
      Re-add the bot
    </InstallLink>
  )

  // A fieldset is a named group, read out with its rows when focus lands on it.
  return (
    <fieldset
      aria-label={`Bot permissions in ${guild.name || "Unnamed server"}`}
      className="admin-bot-health"
      ref={cardRef}
      tabIndex={-1}
    >
      {bot && healthy && !wide ? (
        // Below 1024px a healthy server's rows fold under one line, as FilterBar's fields do, so
        // the Server tab still fits a phone. A Missing row never folds.
        <details>
          <summary className="admin-bot-health-summary">
            Bot permissions: all OK
            <span aria-hidden="true" className="tny-menu-chevron" />
          </summary>
          {rows}
        </details>
      ) : bot ? (
        <>
          {rows}
          {healthy ? null : (
            <Alert action={readd} role="none" tone="warning">
              The bot is missing a permission Ternary needs.
            </Alert>
          )}
        </>
      ) : guild.connected === false ? (
        // The bot is gone (kicked, or never reached): checking again cannot help, re-adding can.
        <Alert action={readd} role="none" tone="warning">
          The bot can't reach this server.
        </Alert>
      ) : (
        <Alert
          action={
            // Busy, not disabled, while it loads: a disabled button would drop focus to the page.
            <Button
              aria-busy={checking || undefined}
              aria-disabled={checking || undefined}
              onClick={() => void check()}
              size="sm"
              variant="secondary"
            >
              {checking ? <span aria-hidden="true" className="tny-spinner" /> : null}
              Check again
            </Button>
          }
          role="none"
          tone="warning"
        >
          {/* The status is the message alone, so its answer is read without the button. */}
          <span role="status">
            {tried
              ? "We still couldn't check this server's permissions."
              : "We couldn't check this server's permissions."}
          </span>
        </Alert>
      )}
    </fieldset>
  )
}
